import { Accordion, Alert, Box, Grid, Heading, Text, useDisclosure } from '@chakra-ui/react';
import { FiCalendar, FiInfo } from 'react-icons/fi';
import { Picker8K } from './pickers/Picker8K';
import { PickerKG } from './pickers/PickerKG';
import { PickerKI } from './pickers/PickerKI';
import { PickerKM } from './pickers/PickerKM';
import { pushDataLayerEvent } from '../../analytics';
import { StudentInfoModal } from './StudentInfoModal';
import { toaster } from '../../components/ui/toaster';
import { useEffect, useState } from 'react';
import { useEnrollment } from './EnrollmentContext';
import { useLocation, useNavigate } from 'react-router-dom';

type SectionConfig = {
	value: string;
	title: string;
	Picker: (props: { onPick: (slot: any) => void }) => JSX.Element;
	showGroupWarning?: boolean;
};

const selectionSections: SectionConfig[] = [
	{
		value: 'grupowe',
		title: 'Korepetycje grupowe',
		Picker: PickerKG,
		showGroupWarning: true,
	},
	{
		value: 'indywidualne',
		title: 'Korepetycje indywidualne',
		Picker: PickerKI,
	},
	{
		value: 'osmoklasisty',
		title: 'Kursy ósmoklasisty',
		Picker: Picker8K,
		showGroupWarning: true,
	},
	{
		value: 'maturalne',
		title: 'Kursy maturalne',
		Picker: PickerKM,
		showGroupWarning: true,
	},
];

const selectionSectionValues = selectionSections.map(({ value }) => value);

const getAccordionValueFromHash = (hash: string) => {
	const normalizedHash = hash.replace(/^#/, '');
	return selectionSectionValues.includes(normalizedHash as (typeof selectionSectionValues)[number])
		? [normalizedHash]
		: [];
};

export const SlotsSelection = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { lessonsList, setLessonsList, showNextStepGuide } = useEnrollment();
	const {
		open: isStudentInfoOpen,
		onOpen: onStudentInfoOpen,
		onClose: onStudentInfoClose,
	} = useDisclosure();
	const [pickedSlot, setPickedSlot] = useState<any>(null);
	const [openAccordionItems, setOpenAccordionItems] = useState<string[]>(() =>
		getAccordionValueFromHash(location.hash)
	);

	useEffect(() => {
		setOpenAccordionItems(getAccordionValueFromHash(location.hash));
	}, [location.hash]);

	const handleAccordionValueChange = (details: { value: string[] }) => {
		const nextValue = details.value ?? [];
		setOpenAccordionItems(nextValue);

		const nextHash = nextValue[0] ? `#${nextValue[0]}` : '';
		if (location.hash !== nextHash) {
			navigate(
				{ pathname: location.pathname, search: location.search, hash: nextHash },
				{ replace: true }
			);
		}
	};

	const handleSlotSelect = (slot: any) => {
		setPickedSlot(slot);
		onStudentInfoOpen();
	};

	const handleEnrollmentAddClose = () => {
		onStudentInfoClose();
		setPickedSlot(null);
	};

	const handleEnrollmentAdd = ({ name, notes }: { name: string; notes: string }) => {
		if (!pickedSlot) return;
		const isFirstLesson = lessonsList.length === 0;

		setLessonsList((previousLessons) => [
			...previousLessons,
			{
				...pickedSlot,
				notes: notes || pickedSlot.notes,
				student: pickedSlot.student ? { ...pickedSlot.student, name } : { name },
			},
		]);
		toaster.create({
			title: 'Dodano zajęcia do listy',
			type: 'success',
			duration: 3000,
		});
		pushDataLayerEvent('add_to_cart');
		handleEnrollmentAddClose();
		if (isFirstLesson) showNextStepGuide();
	};

	return (
		<Grid>
			<Accordion.Root
				collapsible
				variant="outline"
				value={openAccordionItems}
				onValueChange={handleAccordionValueChange}
				borderColor="brand.100"
			>
				{selectionSections.map(({ value, title, Picker, showGroupWarning }) => (
					<Accordion.Item key={value} value={value} borderColor="brand.100">
						<h2>
							<Accordion.ItemTrigger borderColor="brand.100" _expanded={{ borderColor: 'brand.900' }} >
								<Box as="span" flex="1" textAlign="left">
									<Heading
										as="span"
										size="sm"
										fontWeight="semibold"
										textTransform="uppercase"
										letterSpacing="wider"
									>
										{title}
									</Heading>
								</Box>
								<Accordion.ItemIndicator color="brand.100" />
							</Accordion.ItemTrigger>
						</h2>
						<Accordion.ItemContent pb={4} borderColor="brand.100">
							<Accordion.ItemBody pl={3}>
								<Picker onPick={handleSlotSelect} />
								<Alert.Root status="warning" colorPalette="gray" variant="outline" mb={2} size="sm">
									<Alert.Indicator>
										<FiCalendar />
									</Alert.Indicator>
									<Text fontSize="sm">
										We wrześniu, z racji dynamicznie zmieniających się planów zajęć, co tydzień uzupełniamy pulę wolnych terminów.
										Informacje o planowanych zmianach można uzyskać telefonicznie.
									</Text>
								</Alert.Root>
								{showGroupWarning && (
									<Alert.Root status="warning" colorPalette="gray" variant="outline" mb={5} size="sm">
										<Alert.Indicator>
											<FiInfo />
										</Alert.Indicator>
										<Text fontSize="sm">
											Grupa startuje dla min. 3 osób. W przypadku zbyt małej ilości chętnych skontaktujemy się z Państwem w celu przeniesienia do innej grupy.
										</Text>
									</Alert.Root>
								)}
							</Accordion.ItemBody>
						</Accordion.ItemContent>
					</Accordion.Item>
				))}
			</Accordion.Root>
			<StudentInfoModal
				pickedSlot={pickedSlot}
				isOpen={isStudentInfoOpen}
				onClose={handleEnrollmentAddClose}
				onSubmit={handleEnrollmentAdd}
			/>
		</Grid>
	);
};
